import { isRegionalWeatherFront, observedWeatherFrontPosition, type LivingWorldRoute, type ServerWeatherState } from "./weather.js";

/** Public environmental observations only: never player positions, identities or raw receipts. */
export interface WeatherRadarFront {
  id: string;
  preset: string;
  region: string;
  phase: string;
  alpha: number;
  endsAt: number;
  route: LivingWorldRoute & { position: NonNullable<LivingWorldRoute["position"]> };
}
export interface WeatherRadarFrame {
  at: number;
  gameTime: number | null;
  fronts: WeatherRadarFront[];
  aurora: { status: string; night: number; active: boolean } | null;
}
export const WEATHER_HISTORY_RETENTION_MS = 24 * 60 * 60_000;
export const WEATHER_HISTORY_BUCKET_MS = 30_000;

export function weatherRadarFrame(state: ServerWeatherState): WeatherRadarFrame | undefined {
  const at = state.observedAt.getTime(), world = state.livingWorld;
  if (state.source !== "game_server" || !Number.isFinite(at) || !world?.available
    || world.observedAt.getTime() > at || at - world.observedAt.getTime() > 30_000) return undefined;
  const fronts: WeatherRadarFront[] = [];
  for (const front of world.fronts ?? [world]) {
    const endsAt = front.endsAt?.getTime();
    const position = front.route && observedWeatherFrontPosition(front.route, at);
    if (!front.available || !isRegionalWeatherFront(front.effect) || !front.route || !position
      || !["active", "moving", "fading"].includes(front.phase) || !endsAt || endsAt <= at
      || front.observedAt.getTime() > at || at - front.observedAt.getTime() > 30_000) continue;
    const preset = front.regional?.preset ?? ({ stormfront: "rain", snowfront: "snow", dustfront: "dust", marshveil: "fog" }[front.effect]);
    fronts.push({ id: `${front.route.id}:${endsAt}`, preset, region: front.region, phase: front.phase,
      alpha: front.regional?.alpha ?? 1, endsAt, route: { ...front.route, position } });
  }
  const aurora = world.regional?.aurora;
  return { at, gameTime: state.timeOfDay ?? null, fronts,
    aurora: aurora ? { status: aurora.status, night: aurora.night, active: world.effect === "aurora_2d" } : null };
}

export function retainWeatherFrames(frames: WeatherRadarFrame[], frame: WeatherRadarFrame, now = frame.at): WeatherRadarFrame[] {
  const buckets = new Map<number, WeatherRadarFrame>();
  for (const entry of [...frames, frame]) {
    if (!Number.isFinite(entry.at) || entry.at > now || now - entry.at >= WEATHER_HISTORY_RETENTION_MS) continue;
    const bucket = Math.floor(entry.at / WEATHER_HISTORY_BUCKET_MS);
    const previous = buckets.get(bucket);
    if (!previous || previous.at < entry.at) buckets.set(bucket, entry);
  }
  return [...buckets.values()].sort((a, b) => a.at - b.at).slice(-2880);
}

/** Estimate velocity from two native read-backs, never from assumed controller timing. */
export function weatherFrontMotion(front: WeatherRadarFront, history: WeatherRadarFrame[]) {
  const p = front.route.position;
  const prior = [...history].reverse().flatMap(frame => frame.fronts).find(other => other.id === front.id
    && p.observedAt - other.route.position.observedAt >= 5000 && p.observedAt - other.route.position.observedAt <= 120_000);
  if (!prior) return undefined;
  const dt = (p.observedAt - prior.route.position.observedAt) / 1000;
  const vx = (p.x - prior.route.position.x) / dt, vy = (p.y - prior.route.position.y) / dt;
  const speed = Math.hypot(vx, vy);
  const dx = front.route.finish.x - front.route.start.x, dy = front.route.finish.y - front.route.start.y;
  if (!Number.isFinite(speed) || speed < 1 || speed > 3000 || vx * dx + vy * dy <= 0) return undefined;
  return { vx, vy, speedMetresPerSecond: speed / 100,
    bearing: (Math.atan2(vx, -vy) * 180 / Math.PI + 360) % 360 };
}

export function projectWeatherFront(front: WeatherRadarFront, history: WeatherRadarFrame[], at: number): WeatherRadarFront | undefined {
  const p = front.route.position;
  if (at < p.observedAt || at >= front.endsAt || at - p.observedAt > 10 * 60_000) return undefined;
  const motion = weatherFrontMotion(front, history);
  if (!motion) return undefined;
  const remaining = Math.hypot(front.route.finish.x - p.x, front.route.finish.y - p.y);
  const seconds = Math.min((at - p.observedAt) / 1000, remaining / (motion.speedMetresPerSecond * 100));
  return { ...front, route: { ...front.route, position: { x: p.x + motion.vx * seconds, y: p.y + motion.vy * seconds, observedAt: at } } };
}

export interface LocationWeatherWindow {
  id: string; preset: string; active: boolean; arrivalAt: number; clearsBy: number;
  distanceMetres: number; bearing: number | null; strength: "edge" | "core";
}
export function weatherAtLocation(point: { x: number; y: number }, frame: WeatherRadarFrame | undefined,
  history: WeatherRadarFrame[], now: number): { available: boolean; windows: LocationWeatherWindow[] } {
  if (!frame || frame.at > now || now - frame.at > 30_000 || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return { available: false, windows: [] };
  const windows: LocationWeatherWindow[] = [];
  for (const front of frame.fronts) {
    const p = front.route.position, radius = front.route.radiusMetres * 100;
    const x = p.x - point.x, y = p.y - point.y, distance = Math.hypot(x, y);
    if (front.endsAt <= now || now - p.observedAt > 30_000 || p.observedAt > now) continue;
    const active = distance <= radius;
    const motion = weatherFrontMotion(front, history);
    let arrivalAt = now, clearsBy = front.endsAt;
    if (motion) {
      const a = motion.vx ** 2 + motion.vy ** 2, b = 2 * (x * motion.vx + y * motion.vy);
      const disc = b * b - 4 * a * (distance * distance - radius * radius);
      if (disc < 0) { if (!active) continue; }
      else {
        const enter = (-b - Math.sqrt(disc)) / (2 * a), leave = (-b + Math.sqrt(disc)) / (2 * a);
        const travelSeconds = Math.hypot(front.route.finish.x - p.x, front.route.finish.y - p.y) / Math.sqrt(a);
        if (!active && (leave < 0 || enter < 0 || enter > travelSeconds)) continue;
        arrivalAt = active ? now : p.observedAt + enter * 1000;
        if (leave < travelSeconds) clearsBy = Math.min(clearsBy, p.observedAt + leave * 1000);
      }
    } else if (!active) continue;
    if (arrivalAt >= front.endsAt || arrivalAt > now + 10 * 60_000 || clearsBy <= now) continue;
    windows.push({ id: front.id, preset: front.preset, active, arrivalAt: Math.max(now, arrivalAt), clearsBy,
      distanceMetres: Math.max(0, distance - radius) / 100, bearing: motion?.bearing ?? null,
      strength: distance < radius * .55 ? "core" : "edge" });
  }
  return { available: true, windows: windows.sort((a, b) => a.arrivalAt - b.arrivalAt) };
}

const STORM_NAMES = ["Amber", "Bracken", "Cinder", "Drift", "Ember", "Flint", "Gale", "Hollow", "Iris", "Juniper", "Kestrel", "Lumen", "Moss", "Nova", "Onyx", "Petrichor"];
export function weatherEventName(id: string, preset: string): string {
  let hash = 2166136261;
  for (const char of id) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  return `${preset === "thunderstorm" ? "Storm" : preset === "heavy_rain" ? "Rain system" : "Front"} ${STORM_NAMES[hash % STORM_NAMES.length]}`;
}
export function weatherJournal(frames: WeatherRadarFrame[], now: number) {
  const events = new Map<string, { id: string; name: string; preset: string; region: string; firstSeen: number; lastSeen: number; peakAlpha: number; distanceMetres: number; active: boolean; lastPosition: { x: number; y: number }; route: LivingWorldRoute }>();
  for (const frame of [...frames].sort((a,b) => a.at - b.at)) {
    if (frame.at > now || now - frame.at > WEATHER_HISTORY_RETENTION_MS) continue;
    for (const front of frame.fronts) {
      const p = front.route.position, event = events.get(front.id);
      if (!event) events.set(front.id, { id: front.id, name: weatherEventName(front.id, front.preset), preset: front.preset,
        region: front.region, firstSeen: frame.at, lastSeen: frame.at, peakAlpha: front.alpha, distanceMetres: 0,
        active: now - frame.at <= 30_000 && front.endsAt > now, lastPosition: p, route: front.route });
      else {
        if (frame.at - event.lastSeen <= 90_000) event.distanceMetres += Math.hypot(p.x - event.lastPosition.x, p.y - event.lastPosition.y) / 100;
        event.lastPosition = p; event.lastSeen = frame.at; event.peakAlpha = Math.max(event.peakAlpha, front.alpha);
        event.active = now - frame.at <= 30_000 && front.endsAt > now;
      }
    }
  }
  const latest = [...frames].filter(f => f.at <= now).sort((a,b) => b.at-a.at)[0];
  const liveIds = new Set(latest && now-latest.at <= 30_000 ? latest.fronts.map(f => f.id) : []);
  return [...events.values()].map(({ lastPosition: _, ...event }) => ({ ...event, active: event.active && liveIds.has(event.id) }))
    .sort((a,b) => b.lastSeen - a.lastSeen).slice(0, 32);
}
